import { z } from 'zod';
import type { DocumentType } from '@smithy/types';

export const S3LocationSchema = z.object({
  uri: z.string(),
  bucketOwner: z.string().optional(),
});

export const ImageFormatSchema = z.enum(['gif', 'jpeg', 'png', 'webp']);

export const DocumentFormatSchema = z.enum([
  'csv',
  'doc',
  'docx',
  'html',
  'md',
  'pdf',
  'txt',
  'xls',
  'xlsx',
]);

// Mime types Claude can read but that Bedrock doesn't accept under that exact
// label. Normalized at the upload boundary so the persisted mime matches what
// will actually be sent to Bedrock. Most cast to text/plain; a few are aliased
// to their canonical Bedrock-supported form (e.g. text/x-markdown → text/markdown).
export const castToTextMimeTypes: Record<string, string> = {
  // Aliases for Bedrock-supported document formats
  'text/x-markdown': 'text/markdown',

  // RTF has no native Bedrock format; raw markup as plain text is the least-bad option
  'text/rtf': 'text/plain',

  // Structured data
  'text/tab-separated-values': 'text/plain',
  'text/xml': 'text/plain',
  'application/xml': 'text/plain',
  'application/json': 'text/plain',
  'application/x-ndjson': 'text/plain',
  'application/jsonl': 'text/plain',
  'text/jsonl': 'text/plain',
  'application/yaml': 'text/plain',
  'application/x-yaml': 'text/plain',
  'text/yaml': 'text/plain',
  'text/x-yaml': 'text/plain',
  'application/toml': 'text/plain',
  'text/x-toml': 'text/plain',

  // Code
  'text/javascript': 'text/plain',
  'application/x-javascript': 'text/plain',
  'text/x-python': 'text/plain',
  'application/x-python': 'text/plain',
  'application/typescript': 'text/plain',
  'text/typescript': 'text/plain',
  'text/x-typescript': 'text/plain',
  'text/jsx': 'text/plain',
  'text/tsx': 'text/plain',
  'text/x-java-source': 'text/plain',
  'text/x-c': 'text/plain',
  'text/x-c++': 'text/plain',
  'text/x-csharp': 'text/plain',
  'text/x-go': 'text/plain',
  'text/x-rust': 'text/plain',
  'text/x-ruby': 'text/plain',
  'text/x-php': 'text/plain',
  'text/x-sql': 'text/plain',
  'application/sql': 'text/plain',
  'text/x-swift': 'text/plain',
  'text/x-kotlin': 'text/plain',
  'text/x-scala': 'text/plain',
  'text/x-perl': 'text/plain',
  'text/x-lua': 'text/plain',
  'text/x-r': 'text/plain',
  'text/x-matlab': 'text/plain',
  'application/x-httpd-php': 'text/plain',
  'text/coffeescript': 'text/plain',
  'application/graphql': 'text/plain',

  // Shell
  'application/x-sh': 'text/plain',
  'application/x-shellscript': 'text/plain',
  'text/x-shellscript': 'text/plain',
  'text/x-sh': 'text/plain',

  // Stylesheets
  'text/css': 'text/plain',
  'text/x-sass': 'text/plain',
  'text/x-scss': 'text/plain',
  'text/x-less': 'text/plain',

  // Config / other
  'text/x-ini': 'text/plain',
  'text/x-properties': 'text/plain',
  'application/x-wine-extension-ini': 'text/plain',
  'text/x-log': 'text/plain',
  'text/x-diff': 'text/plain',
  'text/x-patch': 'text/plain',
  'text/vcard': 'text/plain',
  'text/calendar': 'text/plain',
  'application/x-www-form-urlencoded': 'text/plain',
};

export const TextBlockSchema = z.object({
  text: z.string(),
});

export const ImageSourceSchema = z.union([
  z.object({ s3Location: S3LocationSchema }),
  z.object({
    bytes: z.custom<Uint8Array>((v) => v instanceof Uint8Array),
  }),
]);

export const ImageBlockSchema = z.object({
  image: z.object({
    format: ImageFormatSchema,
    source: ImageSourceSchema,
  }),
});

export const DocumentSourceSchema = z.union([
  z.object({ s3Location: S3LocationSchema }),
  z.object({
    bytes: z.custom<Uint8Array>((v) => v instanceof Uint8Array),
  }),
]);

export const DocumentBlockSchema = z.object({
  document: z.object({
    format: DocumentFormatSchema,
    name: z.string(),
    source: DocumentSourceSchema,
  }),
});

export const ReasoningTextSchema = z.object({
  text: z.string(),
  signature: z.string().optional(),
});

export const ReasoningBlockSchema = z.object({
  reasoningContent: z.union([
    z.object({ reasoningText: ReasoningTextSchema }),
    z.object({
      redactedContent: z.custom<Uint8Array>((v) => v instanceof Uint8Array),
    }),
  ]),
});

// Bedrock's Converse API only accepts JSON-compatible values in tool payloads.
export const isJsonValue = (v: unknown): v is DocumentType =>
  v === null ||
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean' ||
  (Array.isArray(v) && v.every(isJsonValue)) ||
  (typeof v === 'object' && Object.values(v).every(isJsonValue));

export const isJsonObject = (v: unknown): v is Record<string, DocumentType> =>
  typeof v === 'object' &&
  v !== null &&
  !Array.isArray(v) &&
  Object.values(v).every(isJsonValue);

const JsonDocumentSchema = z.custom<Record<string, DocumentType>>(isJsonObject);

export const ToolUseBlockSchema = z.object({
  toolUse: z.object({
    toolUseId: z.string(),
    name: z.string(),
    input: JsonDocumentSchema,
  }),
});

export const ToolResultBlockSchema = z.object({
  toolResult: z.object({
    toolUseId: z.string(),
    content: z.array(
      z.union([
        z.object({ text: z.string() }),
        z.object({ json: JsonDocumentSchema }),
      ])
    ),
    status: z.enum(['success', 'error']).optional(),
  }),
});

export const ContentBlockSchema = z.union([
  TextBlockSchema,
  ImageBlockSchema,
  DocumentBlockSchema,
  ReasoningBlockSchema,
  ToolUseBlockSchema,
  ToolResultBlockSchema,
]);

export const MessageRoleSchema = z.enum(['assistant', 'user']);

export const MessageSchema = z.object({
  role: MessageRoleSchema,
  content: z.array(ContentBlockSchema),
  stable: z.boolean().optional(),
});

export type S3Location = z.infer<typeof S3LocationSchema>;

export type ImageFormat = z.infer<typeof ImageFormatSchema>;

export type DocumentFormat = z.infer<typeof DocumentFormatSchema>;

export type TextBlock = z.infer<typeof TextBlockSchema>;

export type ImageBlock = z.infer<typeof ImageBlockSchema>;

export type DocumentBlock = z.infer<typeof DocumentBlockSchema>;

export type ReasoningBlock = z.infer<typeof ReasoningBlockSchema>;

export type ToolUseBlock = z.infer<typeof ToolUseBlockSchema>;

export type ToolResultBlock = z.infer<typeof ToolResultBlockSchema>;

export type ContentBlock = z.infer<typeof ContentBlockSchema>;

export type MessageRole = z.infer<typeof MessageRoleSchema>;

export type Message = z.infer<typeof MessageSchema>;

export const isTextBlock = (b: ContentBlock): b is TextBlock => 'text' in b;

export const isImageBlock = (b: ContentBlock): b is ImageBlock => 'image' in b;

export const isDocumentBlock = (b: ContentBlock): b is DocumentBlock =>
  'document' in b;

export const isReasoningBlock = (b: ContentBlock): b is ReasoningBlock =>
  'reasoningContent' in b;

export const isToolUseBlock = (b: ContentBlock): b is ToolUseBlock =>
  'toolUse' in b;

export const isToolResultBlock = (b: ContentBlock): b is ToolResultBlock =>
  'toolResult' in b;

export type ToolInput = ToolUseBlock['toolUse']['input'];

// Nova returns its hidden reasoning as this placeholder text rather than as
// a redactedContent block.
const REDACTED_REASONING = /^\s*\[REDACTED\]\.?\s*$/;

// The model's safety-flag tool; its input never surfaces to a consumer.
const SAFETY_TOOL_NAME = 'flagSafetyConcern';

// Read scopes with a fixed meaning — never interpreted as a tool name.
const RESERVED_READ_SCOPES = new Set([
  'readUserMessages',
  'readModelMessages',
  'readGrounding',
  'readThoughts',
]);

export type ScopeRecord = Record<string, unknown>;

// Scopes arrive as booleans in tokens and as strings in query params.
const isGrantedScopeValue = (value: unknown): boolean =>
  value === true || value === 'true';

/** The session scope that grants reading a tool's stored results:
 * searchPinecone → readSearchPinecone. */
export const toolResultReadScope = (toolName: string): string =>
  `read${toolName.charAt(0).toUpperCase()}${toolName.slice(1)}`;

const isToolResultReadScope = (key: string): boolean =>
  /^read[A-Z]/.test(key) && !RESERVED_READ_SCOPES.has(key);

/** The safety tool's result carries the review outcome the client displays,
 * so it is always readable; every other tool needs its read scope. */
export const canReadToolResult = ({
  toolName,
  scopes,
}: {
  toolName: string;
  scopes: ScopeRecord | null | undefined;
}): boolean => {
  if (toolName === SAFETY_TOOL_NAME) return true;

  const scope = toolResultReadScope(toolName);

  return isToolResultReadScope(scope) && isGrantedScopeValue(scopes?.[scope]);
};

/** The granted per-tool read scopes, for carrying into a retrieval token.
 * Takes token scopes or raw query params. */
export const grantedToolResultScopes = (
  scopes: ScopeRecord | null | undefined
): Record<string, true> =>
  Object.fromEntries(
    Object.entries(scopes ?? {})
      .filter(
        ([key, value]) =>
          isGrantedScopeValue(value) && isToolResultReadScope(key)
      )
      .map(([key]): [string, true] => [key, true])
  );

export type NamedToolResult = { name: string; result: ToolResultBlock };

/** The results the given reader may see. */
export const exposableToolResults = ({
  results,
  scopes,
}: {
  results: NamedToolResult[];
  scopes: ScopeRecord | null | undefined;
}): NamedToolResult[] =>
  results.filter((r) => canReadToolResult({ toolName: r.name, scopes }));

export type ToolResultPart = {
  name: string;
  toolUseId: string;
  status?: 'error' | 'success';
  content?: ToolResultBlock['toolResult']['content'];
};

/** A result as the consumer sees it. The safety result's content is the
 * classifier's decision and reasoning — the client only gets the status. */
export const toolResultPart = ({
  name,
  result,
}: NamedToolResult): ToolResultPart => {
  const { toolUseId, content, status } = result.toolResult;

  return {
    name,
    toolUseId,
    ...(status ? { status } : {}),
    ...(name === SAFETY_TOOL_NAME ? {} : { content }),
  };
};

export type MessagePart =
  | { text: string; thought?: true }
  | { toolResult: ToolResultPart }
  | { toolUse: ToolUseBlock['toolUse'] };

/** The consumer-facing view of a message: answer text, visible reasoning as
 * thought parts (only when the reader is allowed them), and the tool calls
 * the model made. Redacted reasoning never surfaces; a tool call's result
 * surfaces only when passed in `toolResults` (pre-filtered to what the
 * reader may see — exposableToolResults), placed after its call. */
export const contentToParts = ({
  content,
  thoughts = true,
  toolResults = [],
}: {
  content: ContentBlock[];
  thoughts?: boolean;
  toolResults?: NamedToolResult[];
}): MessagePart[] => {
  const resultsByToolUseId = new Map(
    toolResults.map((r) => [r.result.toolResult.toolUseId, r])
  );

  return content.flatMap((b): MessagePart[] => {
    if (isTextBlock(b)) return b.text ? [{ text: b.text }] : [];

    if (isReasoningBlock(b) && 'reasoningText' in b.reasoningContent) {
      const { text } = b.reasoningContent.reasoningText;

      return text && !REDACTED_REASONING.test(text)
        ? [{ text: thoughts ? text : '', thought: true }]
        : [{ text: '', thought: true }];
    }

    if (isToolUseBlock(b)) {
      // The safety tool's input is the model's internal reason for flagging —
      // shown to no one. The result carries what the client displays.
      const toolUse =
        b.toolUse.name === SAFETY_TOOL_NAME
          ? { ...b.toolUse, input: {} }
          : b.toolUse;

      const result = resultsByToolUseId.get(b.toolUse.toolUseId);

      return [
        { toolUse },
        ...(result ? [{ toolResult: toolResultPart(result) }] : []),
      ];
    }

    return [];
  });
};

export const castMimeType = (mimeType: string): string =>
  castToTextMimeTypes[mimeType] ?? mimeType;

const IMAGE_MIME_TO_FORMAT: Record<string, ImageFormat> = {
  'image/jpeg': 'jpeg',
  'image/jpg': 'jpeg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const DOCUMENT_MIME_TO_FORMAT: Record<string, DocumentFormat> = {
  'application/pdf': 'pdf',
  'text/csv': 'csv',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/html': 'html',
  'text/plain': 'txt',
  'text/markdown': 'md',
};

export const anthropicImageMimeTypes: readonly string[] =
  Object.keys(IMAGE_MIME_TO_FORMAT);

export const anthropicDocumentMimeTypes: readonly string[] = Object.keys(
  DOCUMENT_MIME_TO_FORMAT
);

export const imageFormatFromMimeType = (mimeType: string): ImageFormat | null =>
  IMAGE_MIME_TO_FORMAT[castMimeType(mimeType)] ?? null;

export const documentFormatFromMimeType = (
  mimeType: string
): DocumentFormat | null =>
  DOCUMENT_MIME_TO_FORMAT[castMimeType(mimeType)] ?? null;

// Document formats whose bytes are UTF-8 text and can be sent as a text block
// instead of a document block (text blocks don't count toward Bedrock's
// per-request document limit).
const TEXT_DELIVERABLE_FORMATS: ReadonlySet<DocumentFormat> = new Set([
  'txt',
  'md',
  'csv',
  'html',
]);

// Max binary `document` blocks Bedrock accepts in a single Converse request.
export const MAX_DOCUMENT_BLOCKS = 5;

export const isTextDeliverableMimeType = (mimeType: string): boolean => {
  const format = documentFormatFromMimeType(mimeType);

  return format !== null && TEXT_DELIVERABLE_FORMATS.has(format);
};

// True when a file occupies a Bedrock document slot: a supported document that
// is NOT text-deliverable (pdf/doc/docx/xls/xlsx). Images are not documents.
export const isBinaryDocumentMimeType = (mimeType: string): boolean => {
  const format = documentFormatFromMimeType(mimeType);

  return format !== null && !TEXT_DELIVERABLE_FORMATS.has(format);
};
